import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@ibexfusion/models';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { PackageService } from '@services/packages/package.service';

import { AssetSelectorComponent } from '../../../components/asset-selector/asset-selector.component';
import { Observable } from 'rxjs';
import { TranslateService } from '@services/translate/translate.service';
import { OpenReadmeService } from '@services/readme/open-readme.service';
import { EnvironmentService } from '@services/environment/environment.service';
import { MessengerService } from '@services/messenger/messenger.service';

import JsonPointer from 'jsonpointer';

@Component({
    selector: 'app-package-seasonal-images',
    templateUrl: './package-seasonal-images.component.html',
    styleUrls: ['./package-seasonal-images.component.scss'],
    providers: [PackageService],
    standalone: false
})
export class PackageSeasonalImagesComponent implements OnInit, OnDestroy {

  public packageId;
  public myPackage: Models.Package;
  public superadmin = false;

  @Input() onSave: Observable<any>;
  public messages;
  public imageAssetPath: any;
  public mediaAssetPath: any;
  public activeSeasonImage = -1; // default image is active
  private subscription: any;
  private now = { m: new Date().getMonth(), d: new Date().getDate() };

  constructor(
    private route: ActivatedRoute, private snackbar: SnackbarService, public packageService: PackageService, private dialog: MatDialog, private ts: TranslateService,
    private readme: OpenReadmeService, private environmentService: EnvironmentService, private ms: MessengerService
  ) {
    this.imageAssetPath = this.environmentService.getImageAssetPath();
    this.mediaAssetPath = this.environmentService.getMediaAssetPath();
  }

  async ngOnInit() {
    this.messages = this.ts.get('system');
    const privileges = this.ms.$superadminPrivileges.getValue();

    if (!!privileges) {
      for (const privilege of privileges) {
        if (privilege.privilegeName === 'superadmin' && privilege.value === 'packages') {
          this.superadmin = true;
        }
      }
    }
    this.packageId = this.route.snapshot.params['id'];
    this.myPackage = await this.packageService.get(this.packageId)
    this.setActiveSeasonImage();
    this.subscription = this.onSave.subscribe(type => {
      if (type === 'images') {
        this.savePackage();
      }
      if (type === 'images-reload') {
        this.reloadPackage();
      }
    });
  }

  async reloadPackage() {
    this.myPackage = await this.packageService.get(this.packageId);
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  public async savePackage(): Promise<any> {
    for (let i = this.myPackage.tweebie.selectionImages.length - 1; i >= 0; i--) {
      if (!this.myPackage.tweebie.selectionImages[i].assetId) {
        this.myPackage.tweebie.selectionImages.splice(i, 1);
      }
    }
    await this.packageService.update(this.myPackage._id, this.myPackage)
    this.setActiveSeasonImage();
    this.snackbar.success(this.messages.general.savedChanges, 2000);
  }

  async chooseFromImageAssets(targetKey): Promise<any> {
    let dialogRef: MatDialogRef<AssetSelectorComponent>;

    dialogRef = this.dialog.open(AssetSelectorComponent, {
      width: '75%',
      height: '75%',
      data: { type: 'image' }
    });

    const asset: Models.ImageAsset = await dialogRef.afterClosed().toPromise();
    if (!asset) {
      return;
    }
    const format = JsonPointer.get(this.myPackage, targetKey + '/format') || 'freeForm';
    const url = this.imageAssetPath + asset.path + '/' + format + '?t=' + Date.now();
    JsonPointer.set(this.myPackage, targetKey + '/url', url);
    JsonPointer.set(this.myPackage, targetKey + '/assetId', asset._id);
  }

  async chooseFromMediaAssets(targetKey): Promise<any> {
    let dialogRef: MatDialogRef<AssetSelectorComponent>;

    dialogRef = this.dialog.open(AssetSelectorComponent, {
      width: '448px',
      height: '550px',
      data: { type: 'media' }
    });

    const asset: Models.ImageAsset = await dialogRef.afterClosed().toPromise();
    if (!asset) {
      return;
    }
    const url = this.mediaAssetPath + asset.path + '?t=' + Date.now();
    JsonPointer.set(this.myPackage, targetKey, url);
  }

  addSelectionImage() {
    const item = new Models.Image();
    const year = new Date().getFullYear();
    item.from = new Date(year, 0, 1);
    item.to = new Date(year, 11, 31);
    this.myPackage.tweebie.selectionImages.push(item);
  }

  removeSelectionImage(index) {
    this.myPackage.tweebie.selectionImages.splice(index, 1);
  }

  changeSeasonImagesFormat(value, index) {
    const oldUrl = this.myPackage.tweebie.selectionImages[index].url;
    this.myPackage.tweebie.selectionImages[index].url = oldUrl.replace(/\/\w+\?t=[0-9]+/g, '/' + value + '?t=' + Date.now());
  }

  cancelTiming(index) {
    const year = new Date().getFullYear();
    this.myPackage.tweebie.selectionImages[index].from = new Date(year, 0, 1);
    this.myPackage.tweebie.selectionImages[index].to = new Date(year, 11, 31);
  }

  setActiveSeasonImage() {
    this.activeSeasonImage = -1;
    const active = [];
    this.myPackage.tweebie.selectionImages.map((item, index) => {

      const from = item.from ? { m: item.from.getMonth(), d: item.from.getDate() } : null;
      const to = item.to ? { m: item.to.getMonth(), d: item.to.getDate() } : null;

      if (item.disabled || !from || !to) {
        return;
      }
      const info = {
        fromBeforeTo: from.m < to.m || (from.m === to.m && from.d <= to.d),
        fromBeforeNow: from.m < this.now.m || (from.m === this.now.m && from.d <= this.now.d),
        toAfterNow: to.m > this.now.m || (to.m === this.now.m && to.d >= this.now.d)
      };

      if (info.fromBeforeTo) {  // to is in same year
        if (info.fromBeforeNow && info.toAfterNow) {
          active.push({ index: index, from: from, to: to, info: info });
        }
      } else if (info.fromBeforeNow && (to.m < this.now.m || (to.m === this.now.m && to.d >= this.now.d))) { // to is next year and from is this year
        active.push({ index: index, from: from, to: to, info: info });
      } else if (!info.fromBeforeNow && info.toAfterNow) { // to is last year, from is this year
        active.push({ index: index, from: from, to: to, info: info });
      }
    });
    let min = -1;
    active.map(item => {
      let span = -2;
      const days = item.to.d >= item.from.d ? item.to.d - item.from.d : 31 - item.from.d + item.to.d;
      if (item.info.fromBeforeTo) {
        span = (item.to.m - item.from.m) * 100 + days;
      } else { // to is next year
        span = (item.from.m === item.to.m) ? 11 * 100 : (12 - item.from.m + item.to.m) * 100;
        span += days;
      }
      if (min === -1 || span <= min) {
        min = span;
        this.activeSeasonImage = item.index;
      }
    });
  }

  public readMe(name, tag) {
    this.readme.openReadMe({ tag: tag, name: name });
  }
}
